'use client';
import React, { useState, useRef, useEffect } from 'react';

export default function RelayList({
  relays,
  onSelect,
  onCreate,
  onOpenBuyCustomDomain, // This should open buyCustomDomain
  onUseCustomRelay,
}) {
  const [expanded, setExpanded] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const createAreaRef = useRef(null);
  const timer = useRef(null);

  // Check if there's enough room below the create area when expanded.
  useEffect(() => {
    if (expanded && createAreaRef.current) {
      const rect = createAreaRef.current.getBoundingClientRect();
      // Estimate extra height needed for the additional buttons (3 buttons at ~50px each)
      const extraHeight = 150;
      setOpenUpward(window.innerHeight - rect.bottom < extraHeight);
    }
  }, [expanded]);

  const handleMouseEnter = () => {
    if (timer.current) clearTimeout(timer.current);
    setExpanded(true);
  };

  const handleMouseLeave = () => {
    timer.current = setTimeout(() => {
      setExpanded(false);
    }, 300);
  };

  return (
    <div className="max-h-[calc(100vh-200px)] overflow-hidden flex flex-col">
      {/* Alias List (scrollable) */}
      <div className="flex-1 overflow-y-auto">
        <ul className="divide-y divide-white/10">
          {relays.length > 0 ? (
            relays.map((relay, index) => {
              let badgeClasses = "";
              if (relay.status === "Active") {
                badgeClasses =
                  "inline-flex items-center rounded-md bg-green-500/10 px-2 py-1 text-xs font-medium text-green-400 ring-1 ring-green-500/20 ring-inset";
              } else if (relay.status === "Disabled") {
                badgeClasses =
                  "inline-flex items-center rounded-md bg-red-400/10 px-2 py-1 text-xs font-medium text-red-400 ring-1 ring-red-400/20 ring-inset";
              } else if (relay.status === "Ready") {
                badgeClasses =
                  "inline-flex items-center rounded-md bg-red-500/10 px-2 py-1 text-xs font-medium text-red-400 ring-1 ring-red-500/20 ring-inset";
              } else {
                badgeClasses =
                  "inline-flex items-center rounded-md bg-cyan-400/10 px-2 py-1 text-xs font-medium text-cyan-400 ring-1 ring-cyan-400/20 ring-inset";
              }
              const displayName = relay.label || relay.address;
              return (
                <li
                  key={relay.address || index}
                  className="flex items-center justify-between py-3 cursor-pointer hover:bg-white/5 px-3 rounded-md transition-all duration-200"
                  onClick={() => onSelect(relay)}
                  aria-label={`Select alias ${displayName}`}
                >
                  <div>
                    <p className="text-sm font-medium text-white">{displayName}</p>
                    <p className="text-xs text-white/60">Status: {relay.status}</p>
                  </div>
                  <span className={badgeClasses}>
                    {relay.status === "Ready" ? "Inactive" : relay.status}
                  </span>
                </li>
              );
            })
          ) : (
            <p className="text-center text-white/60 py-8">No relays found</p>
          )}
        </ul>
      </div>

      {/* Create Relay Area – always at the bottom, expands with same background */}
      <div
        ref={createAreaRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="overflow-hidden border-t border-white/10 pt-4 mt-4"
        style={{ transition: 'max-height 0.3s', maxHeight: expanded ? '200px' : '50px' }}
      >
        {openUpward ? (
          // If not enough room below, extra buttons appear above the main button.
          <>
            <div className="flex flex-col space-y-2">
              <button
                onClick={() => {
                  setExpanded(false);
                  onOpenBuyCustomDomain(); // Opens buyCustomDomain
                }}
                type="button"
                className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm font-medium text-white backdrop-blur hover:bg-white/20 transition-all duration-200"
              >
                Get a Custom Relay
              </button>
              <button
                onClick={() => {
                  setExpanded(false);
                  onUseCustomRelay();
                }}
                type="button"
                className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm font-medium text-white backdrop-blur hover:bg-white/20 transition-all duration-200"
              >
                Use Custom Relay
              </button>
              <button
                onClick={() => {
                  setExpanded(false);
                  onCreate();
                }}
                type="button"
                className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm font-medium text-white backdrop-blur hover:bg-white/20 transition-all duration-200"
              >
                Use Standard Relay
              </button>
            </div>
            <button
              onClick={() => setExpanded((prev) => !prev)}
              type="button"
              className="w-full rounded-lg bg-indigo-500/20 px-3 py-2 text-sm font-medium text-indigo-300 backdrop-blur hover:bg-indigo-500/30 transition-all duration-200 mt-2"
            >
              + Create Relay
            </button>
          </>
        ) : (
          // Otherwise extra buttons appear below the main button.
          <>
            <button
              onClick={() => setExpanded((prev) => !prev)}
              type="button"
              className="w-full rounded-lg bg-indigo-500/20 px-3 py-2 text-sm font-medium text-indigo-300 backdrop-blur hover:bg-indigo-500/30 transition-all duration-200"
            >
              + Create Relay
            </button>
            {expanded && (
              <div className="flex flex-col space-y-2 mt-2">
                <button
                  onClick={() => {
                    setExpanded(false);
                    onOpenBuyCustomDomain(); // Opens buyCustomDomain
                  }}
                  type="button"
                  className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm font-medium text-white backdrop-blur hover:bg-white/20 transition-all duration-200"
                >
                  Get a Custom Relay
                </button>
                <button
                  onClick={() => {
                    setExpanded(false);
                    onUseCustomRelay();
                  }}
                  type="button"
                  className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm font-medium text-white backdrop-blur hover:bg-white/20 transition-all duration-200"
                >
                  Use Custom Relay
                </button>
                <button
                  onClick={() => {
                    setExpanded(false);
                    onCreate();
                  }}
                  type="button"
                  className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm font-medium text-white backdrop-blur hover:bg-white/20 transition-all duration-200"
                >
                  Use Standard Relay
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}